import type {
  SolarProposalUtilityDto,
  SolarUtilityMonthlyBillDto,
  SolarUtilityRateOptionDto,
  SolarUtilitySideDto,
  UpdateSolarProposalUtilityDto,
} from "~/api/dto/solar-proposal-utility.dto"

export type SolarUtilitySideKey = `post` | `pre`

/**
 * What the seller changed on one side. Null means untouched, so the saved
 * value shows. `override` is the contracted-rate text; an empty string is
 * "use the default". Before solar, `monthlyBill` (a tariff with contracted
 * rates) and `utilityRate` (one without) are V1's other two editors; an empty
 * string is again "use the default".
 */
export type SolarUtilitySideDraft = {
  lseId: number | null
  rateId: string | null
  override: string | null
  monthlyBill: string | null
  utilityRate: string | null
}

export const EMPTY_SIDE_DRAFT: SolarUtilitySideDraft = {
  lseId: null,
  rateId: null,
  override: null,
  monthlyBill: null,
  utilityRate: null,
}

export type SolarUtilityDraft = {
  pre: SolarUtilitySideDraft
  post: SolarUtilitySideDraft
  /** Escalation text; null is untouched. */
  escalation: string | null
}

export const EMPTY_UTILITY_DRAFT: SolarUtilityDraft = {
  pre: EMPTY_SIDE_DRAFT,
  post: EMPTY_SIDE_DRAFT,
  escalation: null,
}

export const MAX_ESCALATION_PCT = 6

/** A contracted rate is entered in $/kWh, as stored. */
const formatRate = (rate: number): string => String(rate)

/** A utility rate shows to the hundredth of a cent, as it compares. */
const roundRate = (rate: number): number => Number(rate.toFixed(4))

/**
 * V1's Monthly Bill conversion: a contracted-rate change moves the year-one
 * bill by its difference over the year's usage, and the effective rate by the
 * same difference.
 */
export const monthlyBillFromRate = (
  baseline: SolarUtilityMonthlyBillDto,
  contractedRate: number,
): number =>
  baseline.monthlyBill +
  ((contractedRate - baseline.contractedRate) * baseline.annualKwh) / 12

export const contractedRateFromBill = (
  baseline: SolarUtilityMonthlyBillDto,
  monthlyBill: number,
): number =>
  baseline.contractedRate +
  ((monthlyBill - baseline.monthlyBill) * 12) / baseline.annualKwh

export const effectiveRateFor = (
  baseline: SolarUtilityMonthlyBillDto,
  contractedRate: number,
): number =>
  Math.max(0, baseline.effectiveRate + contractedRate - baseline.contractedRate)

/** The lowest whole-dollar bill whose contracted rate isn't negative. */
export const minimumMonthlyBill = (
  baseline: SolarUtilityMonthlyBillDto,
): number => Math.max(0, Math.ceil(monthlyBillFromRate(baseline, 0) - 1e-9))

/**
 * How the pre-solar rate is edited. A tariff with contracted rates takes a
 * Monthly Bill, or before its first savings run (or a just-picked tariff) a
 * contracted rate. One without takes V1's utility rate once a savings run on
 * it knows Genability's rate; until then there is nothing to edit.
 */
export type SolarUtilityRateEditor =
  | `contractedRate`
  | `monthlyBill`
  | `none`
  | `utilityRate`
  | `utilityRatePending`

/** One side as the form shows it: the draft over the saved selection. */
export type SolarUtilitySideView = {
  lseId: number | null
  rate: SolarUtilityRateOptionDto | null
  /** The company's rates on this side, "Best match" first. */
  rates: SolarUtilityRateOptionDto[]
  defaultRateId: string | null
  /** The selected tariff takes a contracted rate. */
  contracted: boolean
  overrideText: string
  /** The default the empty input stands for, $/kWh. */
  defaultContractedRate: number | null
  editor: SolarUtilityRateEditor
  /** For `monthlyBill`: the bill text, the bill Save leaves, and the rates it means. */
  monthlyBill: {
    baseline: SolarUtilityMonthlyBillDto
    text: string
    /** The bill the saved override shows as; null when the default is saved. */
    savedBill: number | null
    defaultBill: number
    minimum: number
    /** The contracted rate the bill text means; null is the default. */
    contractedRate: number | null
    effectiveRate: number
  } | null
  /** For `utilityRate`: the rate text and Genability's rate it falls back to. */
  utilityRate: { text: string; base: number; custom: boolean } | null
  /**
   * For `utilityRatePending` on the saved tariff: a seller's adjustment is
   * saved (carried over from another tariff), which can still be reset.
   */
  pendingCustomRate: boolean
}

/** A Monthly Bill is whole dollars, as in V1: it shows and validates rounded. */
const parseBill = (text: string): number | null | undefined => {
  const trimmed = text.trim()
  if (trimmed === ``) return null
  return /^\d+$/.test(trimmed) ? Number(trimmed) : undefined
}

export const solarUtilitySideView = (
  side: SolarUtilitySideDto,
  draft: SolarUtilitySideDraft,
  beforeSolar = false,
): SolarUtilitySideView => {
  const lseId = draft.lseId ?? side.company?.lseId ?? null
  const company = side.companies.find((entry) => entry.lseId === lseId)
  const defaultRateId = company?.defaultRateId ?? null
  const rates = side.rates
    .filter((rate) => rate.lseId === lseId)
    .sort(
      (a, b) => Number(b.id === defaultRateId) - Number(a.id === defaultRateId),
    )
  // A saved rate the options no longer list (a retired tariff) still shows
  // as selected until the seller picks another.
  const rate =
    draft.rateId != null
      ? (rates.find((option) => option.id === draft.rateId) ?? null)
      : draft.lseId != null
        ? null
        : side.rate
  const contracted = rate?.hasContractedRates === true
  const savedOverride = side.contractedRate?.override ?? null
  const defaultContractedRate = side.contractedRate?.default ?? null
  // The baselines describe the saved tariff, so only it edits against them.
  const savedTariff = rate != null && rate.id === side.rate?.id
  const editor: SolarUtilityRateEditor = contracted
    ? savedTariff && side.monthlyBill
      ? `monthlyBill`
      : `contractedRate`
    : !beforeSolar || rate == null
      ? `none`
      : savedTariff &&
          side.utilityRate?.base != null &&
          side.utilityRate.rate != null
        ? `utilityRate`
        : `utilityRatePending`

  let monthlyBill: SolarUtilitySideView[`monthlyBill`] = null
  if (editor === `monthlyBill` && side.monthlyBill) {
    const baseline = side.monthlyBill
    const fallbackRate = defaultContractedRate ?? baseline.contractedRate
    const savedBill =
      savedOverride == null
        ? null
        : Math.round(monthlyBillFromRate(baseline, savedOverride))
    const text =
      draft.monthlyBill ?? (savedBill == null ? `` : String(savedBill))
    const bill = parseBill(text)
    const contractedRate =
      bill == null
        ? bill === null
          ? null
          : savedOverride
        : contractedRateFromBill(baseline, bill)
    monthlyBill = {
      baseline,
      text,
      savedBill,
      defaultBill: Math.round(monthlyBillFromRate(baseline, fallbackRate)),
      minimum: minimumMonthlyBill(baseline),
      contractedRate,
      effectiveRate: effectiveRateFor(baseline, contractedRate ?? fallbackRate),
    }
  }

  const utilityRate =
    editor === `utilityRate` &&
    side.utilityRate?.base != null &&
    side.utilityRate.rate != null
      ? {
          text: draft.utilityRate ?? String(roundRate(side.utilityRate.rate)),
          base: side.utilityRate.base,
          custom: side.utilityRate.custom,
        }
      : null

  return {
    lseId,
    rate,
    rates,
    defaultRateId,
    contracted,
    overrideText:
      draft.override ?? (savedOverride == null ? `` : formatRate(savedOverride)),
    defaultContractedRate,
    editor,
    monthlyBill,
    utilityRate,
    pendingCustomRate:
      editor === `utilityRatePending` &&
      savedTariff &&
      side.utilityRate?.custom === true,
  }
}

/** Empty is the default (null); anything else must be a number of $0 or more. */
export const parseContractedRate = (
  text: string,
): { value: null; error: string } | { value: number | null; error: null } => {
  const trimmed = text.trim()
  if (trimmed === ``) {
    return { value: null, error: null }
  }
  const value = Number(trimmed)
  return Number.isFinite(value) && value >= 0 && /^\d*\.?\d*$/.test(trimmed)
    ? { value, error: null }
    : { value: null, error: `Enter a rate of $0 or more, or leave it empty.` }
}

export const parseEscalation = (
  text: string,
): { value: null; error: string } | { value: number; error: null } => {
  const trimmed = text.trim()
  const value = Number(trimmed)
  return /^\d+$/.test(trimmed) && value <= MAX_ESCALATION_PCT
    ? { value, error: null }
    : {
        value: null,
        error: `Enter a whole percent from 0 to ${MAX_ESCALATION_PCT}.`,
      }
}

export type SolarUtilityFormState = {
  pre: SolarUtilitySideView
  post: SolarUtilitySideView
  escalationText: string
  errors: {
    preOverride: string | null
    postOverride: string | null
    preMonthlyBill: string | null
    preUtilityRate: string | null
    escalation: string | null
  }
  /** Only the changed fields; empty when nothing changed. */
  body: UpdateSolarProposalUtilityDto
  /** A picked company has a rate picked too, and every input parses. */
  valid: boolean
}

const sideChanges = (
  key: SolarUtilitySideKey,
  side: SolarUtilitySideDto,
  view: SolarUtilitySideView,
  override: ReturnType<typeof parseContractedRate>,
): UpdateSolarProposalUtilityDto => {
  const overrideField =
    key === `pre` ? `preContractedOverride` : `postContractedOverride`
  const savedOverride = side.contractedRate?.override ?? null
  return {
    ...(view.rate && view.rate.id !== side.rate?.id
      ? { [key]: { rateId: view.rate.id } }
      : {}),
    // A tariff without contracted rates takes no override; the server drops
    // the saved one when the side switches to it.
    ...(view.editor === `contractedRate` &&
    override.error == null &&
    override.value !== savedOverride
      ? { [overrideField]: override.value }
      : {}),
  }
}

/** Before solar: the Monthly Bill as its contracted rate, and V1's utility rate. */
const preRateChanges = (
  side: SolarUtilitySideDto,
  view: SolarUtilitySideView,
  draft: SolarUtilitySideDraft,
  errors: { preMonthlyBill: string | null; preUtilityRate: string | null },
): UpdateSolarProposalUtilityDto => {
  const changes: UpdateSolarProposalUtilityDto = {}
  const bill =
    view.monthlyBill && draft.monthlyBill != null
      ? parseBill(draft.monthlyBill)
      : undefined
  // Typing the bill already shown (a rounded saved override, or the default)
  // changes nothing.
  const sameBill =
    view.monthlyBill != null &&
    bill != null &&
    bill === (view.monthlyBill.savedBill ?? view.monthlyBill.defaultBill)
  if (
    view.monthlyBill &&
    draft.monthlyBill != null &&
    errors.preMonthlyBill == null &&
    !sameBill
  ) {
    const rate =
      view.monthlyBill.contractedRate == null
        ? null
        : Number(view.monthlyBill.contractedRate.toFixed(6))
    if (rate !== (side.contractedRate?.override ?? null)) {
      changes.preContractedOverride = rate
    }
  }
  if (view.pendingCustomRate && draft.utilityRate === ``) {
    changes.preUtilityRate = null
  }
  if (
    view.utilityRate &&
    draft.utilityRate != null &&
    errors.preUtilityRate == null
  ) {
    const parsed = parseContractedRate(draft.utilityRate).value
    if (parsed == null) {
      if (view.utilityRate.custom) changes.preUtilityRate = null
    } else if (
      roundRate(parsed) !== roundRate(side.utilityRate?.rate ?? Number.NaN)
    ) {
      changes.preUtilityRate = parsed
    }
  }
  return changes
}

/** The form's view of the saved utility, the seller's draft, and the PUT body. */
export const solarUtilityFormState = (
  utility: SolarProposalUtilityDto,
  draft: SolarUtilityDraft,
): SolarUtilityFormState => {
  const pre = solarUtilitySideView(utility.pre, draft.pre, true)
  const post = solarUtilitySideView(utility.post, draft.post)
  const preOverride = parseContractedRate(pre.overrideText)
  const postOverride = parseContractedRate(post.overrideText)
  const escalationText = draft.escalation ?? String(utility.escalationPct)
  const escalation = parseEscalation(escalationText)
  const bill = pre.monthlyBill ? parseBill(pre.monthlyBill.text) : null
  const errors = {
    preOverride: pre.editor === `contractedRate` ? preOverride.error : null,
    postOverride: post.contracted ? postOverride.error : null,
    preMonthlyBill:
      pre.monthlyBill == null || draft.pre.monthlyBill == null
        ? null
        : bill === undefined
          ? `Enter a whole-dollar monthly bill, or leave it empty.`
          : bill !== null && bill < pre.monthlyBill.minimum
            ? `The monthly bill can't be below $${pre.monthlyBill.minimum}.`
            : null,
    preUtilityRate:
      pre.utilityRate == null || draft.pre.utilityRate == null
        ? null
        : parseContractedRate(draft.pre.utilityRate).error == null
          ? null
          : `Enter a rate of $0 or more, or leave it empty.`,
    escalation: draft.escalation == null ? null : escalation.error,
  }

  return {
    pre,
    post,
    escalationText,
    errors,
    body: {
      ...sideChanges(`pre`, utility.pre, pre, preOverride),
      ...preRateChanges(utility.pre, pre, draft.pre, errors),
      ...sideChanges(`post`, utility.post, post, postOverride),
      ...(escalation.value != null && escalation.value !== utility.escalationPct
        ? { escalationPct: escalation.value }
        : {}),
    },
    valid:
      (draft.pre.lseId == null || pre.rate != null) &&
      (draft.post.lseId == null || post.rate != null) &&
      Object.values(errors).every((error) => error == null),
  }
}
